import { ChangeEvent, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Field, Input } from '@matillion/component-library'

import {
  ParameterOverlayButton,
  ParameterOverlayFooter,
  ParameterOverlayHeader
} from 'components/ParameterOverlay'

import {
  StorageEditorType,
  UrlEditorProps
} from 'modules/ParameterEditors/types'

import classes from '../../UrlEditor.module.scss'
import ContainerList from '../ContainerList/ContainerList'
import { ErrorMessage } from '../ErrorMessage/ErrorMessage'
import { useGetContainers } from '../hooks/useGetContainers'
import { useGrid } from '../hooks/useGrid'

const AZUREPREFIX = 'azure://'

export const AzureUrlEditor = ({
  value,
  onDone,
  parameterName
}: UrlEditorProps) => {
  const initialAzureUrl = value[0]?.replace(AZUREPREFIX, '') || ''
  const initialFullPath = initialAzureUrl.split('/').filter(Boolean)

  const { t } = useTranslation()
  const [warehouseUrl, setWarehouseUrl] = useState<string>(initialAzureUrl)
  const [error, setError] = useState<string | null>()
  const [fullPath, setFullPath] = useState<string[]>(initialFullPath)

  const onContainerPathChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setWarehouseUrl(e.target.value)
    },
    [setWarehouseUrl]
  )

  const { isLoading, buckets, folders, foldersError, bucketsError } =
    useGetContainers(fullPath, StorageEditorType.AZURE)

  const { rows, columns, setSort } = useGrid({
    folders: folders ?? [],
    buckets: buckets ?? [],
    fullPath,
    setFullPath,
    setWarehouseUrl
  })

  const handleError = (errorMessage: string) => {
    setError(errorMessage)
    setFullPath([])
  }

  useEffect(() => {
    if (foldersError && foldersError.status === 403) {
      handleError(
        `${t('parameterEditor.AZURE_URL.error.accessDenied')} ${
          foldersError.detail
        }`
      )
    } else if (foldersError) {
      handleError(
        `${t('parameterEditor.AZURE_URL.error.foldersError')} ${
          foldersError.detail
        }`
      )
    } else if (bucketsError) {
      handleError(
        `${t('parameterEditor.AZURE_URL.error.bucketsError')} ${
          bucketsError.detail
        }`
      )
    }
  }, [foldersError, setError, bucketsError, warehouseUrl, t, error])

  useEffect(() => {
    if (warehouseUrl) {
      setError(null)
    }
  }, [warehouseUrl])

  const onSubmit = useCallback(() => {
    const newUrl = warehouseUrl.toLowerCase().includes(AZUREPREFIX)
      ? warehouseUrl
      : `${AZUREPREFIX}${warehouseUrl}`
    onDone([newUrl])
  }, [warehouseUrl, onDone])

  const handleEnterPress = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        setFullPath(warehouseUrl.split('/').filter(Boolean))
      }
    },
    [warehouseUrl]
  )

  const handleBlur = () => {
    if (isLoading) return

    if (!warehouseUrl) {
      setError(t('parameterEditor.AZURE_URL.error.emptyValue'))
    }
  }

  return (
    <>
      <ParameterOverlayHeader title={parameterName} />

      <Field
        id="azureurleditor-url"
        name="azureurleditor-url"
        title={t('parameterEditor.AZURE_URL.url.title')}
        aria-label={t('parameterEditor.AZURE_URL.url.title')}
        placeholder={t('parameterEditor.AZURE_URL.url.placeholder')}
        prefix={AZUREPREFIX.toUpperCase()}
        value={warehouseUrl}
        onChange={onContainerPathChange}
        onKeyPress={handleEnterPress}
        onBlur={handleBlur}
        hasError={!!error}
        error={!!error}
        errorText={
          error && (
            <ErrorMessage
              storageEditorType={StorageEditorType.AZURE}
              text={error}
            />
          )
        }
        inputComponent={Input}
        className={classes['Buckets--Input']}
      />
      <ContainerList
        isLoading={isLoading}
        columns={columns}
        rows={rows}
        setSort={setSort}
        fullPath={fullPath}
        setFullPath={setFullPath}
        setWarehouseUrl={setWarehouseUrl}
        storageEditorType={StorageEditorType.AZURE}
      />

      <ParameterOverlayFooter>
        <ParameterOverlayButton onClick={onSubmit} text={t('common.save')} />
      </ParameterOverlayFooter>
    </>
  )
}
